import { RightOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import type { IWorkJourneyManagementCalendarCell } from '@src/models/interfaces/workJourneyManagement.interface';

import styles from './styles.module.less';

interface WorkJourneyCalendarDayCellProps {
  cell: IWorkJourneyManagementCalendarCell;
  onFooterClick?: (cell: IWorkJourneyManagementCalendarCell) => void;
}

const HIGHLIGHT_CLASS: Record<IWorkJourneyManagementCalendarCell['highlight'], string> = {
  critical: styles.highlightCritical,
  warning: styles.highlightWarning,
  rejections: styles.highlightRejections,
  holiday: styles.highlightHoliday,
  today: styles.highlightToday,
  normal: '',
};

const INDICATOR_DOT_CLASS: Record<string, string> = {
  below_minimum: styles.dotCritical,
  scheduled_absences: styles.dotWarning,
  rejections: styles.dotRejections,
};

export const WorkJourneyCalendarDayCell = ({
  cell,
  onFooterClick,
}: WorkJourneyCalendarDayCellProps) => {
  const intl = useIntl();

  const showJourneysFooter = cell.footerAction === 'journeys' && cell.journeyCount > 0;
  const showReportFooter = cell.footerAction === 'report';
  const showFooter = cell.isCurrentMonth && (showReportFooter || showJourneysFooter);

  const footerLabel = showReportFooter
    ? intl.formatMessage({ id: 'workJourney.management.calendar.footer.report' })
    : intl.formatMessage(
        { id: 'workJourney.management.calendar.footer.journeys' },
        { count: cell.journeyCount },
      );

  return (
    <article
      className={classNames(
        styles.cell,
        !cell.isCurrentMonth && styles.cellOutsideMonth,
        HIGHLIGHT_CLASS[cell.highlight],
      )}
    >
      <header className={styles.cellHeader}>
        <span className={styles.dayNumber}>
          {intl.formatNumber(cell.day, { minimumIntegerDigits: 2 })}
        </span>
        {cell.isToday && (
          <span className={styles.todayBadge}>
            {intl.formatMessage({ id: 'workJourney.management.calendar.today' })}
          </span>
        )}
      </header>

      <div className={styles.cellBody}>
        {(cell.holidayLabel || cell.highlight === 'holiday') && (
          <div className={styles.holidayRow}>
            <span className={`${styles.dot} ${styles.dotHoliday}`} />
            <span className={styles.indicatorLabel}>
              {cell.holidayLabel ||
                intl.formatMessage({ id: 'workJourney.management.calendar.sidebar.issue.holiday' })}
            </span>
          </div>
        )}

        {cell.indicators.map((indicator, index) => (
          <div key={`${indicator.type}-${index}`} className={styles.indicatorRow}>
            <span className={`${styles.dot} ${INDICATOR_DOT_CLASS[indicator.type] ?? ''}`} />
            <span className={styles.indicatorLabel}>
              {indicator.label ??
                intl.formatMessage(
                  { id: `workJourney.management.calendar.indicator.${indicator.type}` },
                  { count: indicator.count },
                )}
            </span>
          </div>
        ))}
      </div>

      {showFooter ? (
        <footer className={styles.cellFooter}>
          <button type="button" className={styles.footerLink} onClick={() => onFooterClick?.(cell)}>
            {footerLabel}
            <RightOutlined className={styles.footerIcon} />
          </button>
        </footer>
      ) : null}
    </article>
  );
};
